import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { ApiWorkflowDialog, DatePicker } from '@verantos-dev/shared-components';
import { borderRadius, fontSize } from '@verantos-dev/shared-ui-components/tokens';

import {
  type IndexDateTab,
  type OccurrenceType,
  useSetIndexDateModal,
} from './useSetIndexDateModal';
import { AvLibraryListWithSearch } from '../AddVariableFromLibraryModal/AvLibraryListWithSearch';

interface SetIndexDateModalProps {
  onClose: () => void;
}

export const SetIndexDateModal = ({ onClose }: SetIndexDateModalProps) => {
  const intl = useIntl();
  const {
    tab,
    selectedAvId,
    occurrence,
    nthValue,
    calendarDate,
    isSubmitting,
    submitError,
    searchQuery,
    isLoading,
    isError,
    filteredItems,
    canSubmit,
    isGen3,
    inCohortAvIds,
    nameConflictAvIds,
    handleTabChange,
    handleSelectAv,
    handleOccurrenceChange,
    handleNthValueChange,
    handleNthValueBlur,
    handleCalendarDateChange,
    handleSearchChange,
    handleSubmit,
    handleClose,
  } = useSetIndexDateModal(onClose);

  const renderVariableTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* description */}
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({
          id: 'projects.analyticalVariables.library.setIndexDate.variableDescription',
        })}
      </Typography>

      {/* search + list */}
      <AvLibraryListWithSearch
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        items={filteredItems}
        isLoading={isLoading}
        isError={isError}
        multiple={false}
        onSelect={handleSelectAv}
        isSelected={(id) => selectedAvId === id}
        inCohortAvIds={inCohortAvIds}
        nameConflictAvIds={nameConflictAvIds}
        maxHeight={selectedAvId && !isGen3 ? 240 : undefined}
      />

      {selectedAvId && !isGen3 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 3 }}>
            {intl.formatMessage({
              id: 'projects.analyticalVariables.library.setIndexDate.occurrenceLabel',
            })}
          </Typography>
          <RadioGroup
            value={occurrence}
            onChange={(e) => handleOccurrenceChange(e.target.value as OccurrenceType)}
          >
            <FormControlLabel
              value="first"
              control={<Radio size="small" />}
              label={intl.formatMessage({
                id: 'projects.analyticalVariables.library.setIndexDate.occurrenceFirst',
              })}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FormControlLabel
                value="nth"
                control={<Radio size="small" />}
                label={intl.formatMessage({
                  id: 'projects.analyticalVariables.library.setIndexDate.occurrenceNth',
                })}
              />
              <TextField
                type="number"
                size="small"
                value={nthValue}
                disabled={occurrence !== 'nth'}
                onChange={(e) => handleNthValueChange(e.target.value)}
                onBlur={handleNthValueBlur}
                slotProps={{ htmlInput: { min: 1 } }}
                sx={{ width: 100 }}
              />
            </Box>
            <FormControlLabel
              value="last"
              control={<Radio size="small" />}
              label={intl.formatMessage({
                id: 'projects.analyticalVariables.library.setIndexDate.occurrenceLast',
              })}
            />
          </RadioGroup>
        </Box>
      )}
    </Box>
  );

  const renderCalendarTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({
          id: 'projects.analyticalVariables.library.setIndexDate.calendarDescription',
        })}
      </Typography>
      <Box mt={2}>
        <DatePicker
          id="index-date-calendar"
          displayLabel={intl.formatMessage({
            id: 'projects.analyticalVariables.library.setIndexDate.calendarLabel',
          })}
          value={calendarDate}
          onChange={handleCalendarDateChange}
          width="dialog"
        />
      </Box>
    </Box>
  );

  return (
    <ApiWorkflowDialog
      id="set-index-date-modal"
      data-testid="set-index-date-modal"
      title={intl.formatMessage({
        id: 'projects.analyticalVariables.library.setIndexDate.label',
      })}
      onClose={handleClose}
      onSubmit={handleSubmit}
      isValidForSubmit={canSubmit}
      isSubmitting={isSubmitting}
      submittingError={submitError ? { message: 'error' } : undefined}
      size="medium"
      dialogMessages={{
        entrySaveButtonText: intl.formatMessage({
          id: 'projects.analyticalVariables.library.setIndexDate.label',
        }),
        entryCancelButtonText: intl.formatMessage({
          id: 'projects.analyticalVariables.library.cancel',
        }),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Tabs
          value={tab}
          onChange={(_, v: IndexDateTab) => handleTabChange(v)}
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[300],
            borderRadius: `${borderRadius.md}px`,
            p: '2px', // TODO: no VDS token for this value — needs designer input
            minHeight: 36,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              flex: 1,
              minHeight: 24,
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              borderRadius: `${borderRadius.sm}px`,
              px: 4,
              py: '6px', // TODO: no VDS token for this value — needs designer input
              fontSize: fontSize[14],
              lineHeight: '24px', // TODO: no VDS token for this value — needs designer input
            },
            '& .Mui-selected': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[2],
              borderRadius: `${borderRadius.sm}px`,
            },
          })}
        >
          <Tab
            value="variable"
            label={intl.formatMessage({
              id: 'projects.analyticalVariables.library.setIndexDate.tabVariable',
            })}
          />
          <Tab
            value="calendar"
            label={intl.formatMessage({
              id: 'projects.analyticalVariables.library.setIndexDate.tabCalendar',
            })}
          />
        </Tabs>

        {tab === 'variable' ? renderVariableTab() : renderCalendarTab()}
      </Box>
    </ApiWorkflowDialog>
  );
};
